// src/lib/firmPerformance.ts: weighted YTD return across every fund a manager
// runs, the manager-side mirror of the allocator's portfolioCalc.
import { supabase } from '../services/supabase';

export interface FundYTD {
  fundId: string;
  fundName: string;
  ytdReturn: number | null;
  aum: number | null;
  currency: string;
}

export interface FirmYTDResult {
  weightedYTD: number | null;
  fundCount: number;
  fundsWithData: number;
  funds: FundYTD[];
  totalAUM: number | null;
  coverageLabel: string;
}

const empty: FirmYTDResult = {
  weightedYTD: null, fundCount: 0, fundsWithData: 0,
  funds: [], totalAUM: null, coverageLabel: 'No active funds',
};

/** aum_min / aum_max are the reported band; the midpoint stands in for a fund
 *  that has not published an exact figure. Values are in millions. */
function fundAUM(f: { aum_exact: number | null; aum_min: number | null; aum_max: number | null }): number | null {
  if (f.aum_exact) return Number(f.aum_exact);
  if (f.aum_min && f.aum_max) return (Number(f.aum_min) + Number(f.aum_max)) / 2;
  return f.aum_min ? Number(f.aum_min) : f.aum_max ? Number(f.aum_max) : null;
}

/**
 * Weighted YTD across the firm's open funds.
 *
 * Weighting is by AUM, but only when every fund that has performance data also
 * has an AUM figure. A mixed book cannot be weighted coherently: a fund with no
 * reported AUM has no weight to contribute, and giving it a nominal one would
 * let a $2bn fund and an unsized fund count almost identically. So the moment
 * one contributing fund is unsized the whole calculation falls back to equal
 * weighting, which is at least a rule the number can be read against.
 *
 * The average is taken over the funds that actually reported, not all open
 * funds, so an unreported fund cannot drag the figure toward zero. Callers
 * should show coverageLabel alongside the number.
 */
export async function calculateFirmYTD(firmId: string): Promise<FirmYTDResult> {
  const currentYear = new Date().getFullYear();

  const { data: funds, error } = await supabase
    .from('funds')
    .select('id, name, aum_min, aum_max, aum_exact')
    .eq('firm_id', firmId)
    .eq('status', 'open')
    .order('name');

  if (error || !funds || funds.length === 0) return empty;

  // One round trip for the whole firm rather than one per fund.
  const { data: perf } = await supabase
    .from('fund_performance')
    .select('fund_id, month, return_pct')
    .in('fund_id', funds.map(f => f.id))
    .eq('year', currentYear)
    .order('month', { ascending: true });

  const byFund: Record<string, number[]> = {};
  ((perf as any[]) || []).forEach(p => {
    if (!byFund[p.fund_id]) byFund[p.fund_id] = [];
    byFund[p.fund_id].push(Number(p.return_pct) || 0);
  });

  let totalAUM = 0;
  const results: FundYTD[] = (funds as any[]).map(f => {
    const aum = fundAUM(f);
    if (aum) totalAUM += aum;

    const months = byFund[f.id];
    const ytd = months && months.length
      ? (months.reduce((acc, r) => acc * (1 + r / 100), 1) - 1) * 100
      : null;

    // funds.currency does not exist in the schema, every fund is reported in
    // USD today. Kept on the interface so a currency column can land later.
    return { fundId: f.id, fundName: f.name, ytdReturn: ytd, aum, currency: 'USD' };
  });

  const covered = results.filter(f => f.ytdReturn !== null);
  const fundsWithData = covered.length;

  let weightedYTD: number | null = null;
  if (fundsWithData > 0) {
    const coveredAUM = covered.reduce((sum, f) => sum + (f.aum || 0), 0);
    const canWeight = covered.every(f => f.aum) && coveredAUM > 0;
    weightedYTD = canWeight
      ? covered.reduce((acc, f) => acc + ((f.aum as number) / coveredAUM) * (f.ytdReturn as number), 0)
      : covered.reduce((acc, f) => acc + (f.ytdReturn as number), 0) / fundsWithData;
  }

  const plural = funds.length > 1 ? 's' : '';
  const coverageLabel = fundsWithData === 0
    ? 'No performance data yet'
    : fundsWithData === funds.length
      ? `${funds.length} fund${plural}`
      : `${fundsWithData} of ${funds.length} funds`;

  return {
    weightedYTD,
    fundCount: funds.length,
    fundsWithData,
    funds: results,
    totalAUM: totalAUM > 0 ? totalAUM : null,
    coverageLabel,
  };
}

/** Fund AUM is stored in millions, matching the `$120mm` form used elsewhere. */
export function formatFundAUM(aum: number | null, currency: string = 'USD'): string {
  if (!aum) return '-';
  const symbol = currency === 'GBP' ? '£' : currency === 'EUR' ? '€' : '$';
  if (aum >= 1000) return `${symbol}${(aum / 1000).toFixed(1)}bn`;
  return `${symbol}${Math.round(aum)}mm`;
}
